import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { FormProvider, useFieldArray, useWatch } from 'react-hook-form';
import { useHotkeys } from 'react-hotkeys-hook';

import {
  DualButton,
  ImagePreviewCard,
  RhfDropdown,
  RhfTextField
} from '@components';
import {
  AMOUNT_FIELD_MAX_LENGTH,
  DEAL_HIGHLIGHTS_TITLE_LENGTH,
  DEAL_TERMS_MAX_LENGTH,
  DealModeType,
  IMAGE_FORMATS,
  MAXIMUM_INVESTMENT_MAX_AMOUNT,
  MINIMUM_INVESTMENT_MAX_AMOUNT,
  PreIPODealStatus,
  PreIPODealTypes,
  PreIPOModeType,
  RegularExpressions
} from '@constants/common';
import {
  formatAmount,
  getFormattedAmount,
  getNumberFromString
} from '@utils/amount';
import { translate, translateDynamic } from '@utils/locale';
import { InfoIcon, PlusIcon, TrashIcon } from '@assets/icons';
import { Backdrop, Button, CircularProgress, Tooltip } from '@mui/material';
import { generateFileUrl, getNumbersOnly } from '@utils/common';
import { colors } from '@constants/colors';
import DateTimePicker from '@components/date-picker/DateTimePicker';
import FileUploader from '@containers/file-uploader/FileUploader';

import { PreIPODealDetailsFormProps, PreIPODealFormValues } from '../types';
import { isPreIPOBottomActionDisabled } from '../common';

const PreIPODealDetailsForm: FC<PreIPODealDetailsFormProps> = ({
  methods,
  errors,
  handleNextClick,
  isUpdatePreIPOLoading,
  handleSaveAsDraftClick,
  isGetPreIPOLoading = false
}) => {
  const { control, setValue, getValues, clearErrors } = methods;

  const [deleteClickedFile, setDeleteClickedFile] = useState<string>('');

  const [dealStartDate, dealEndDate] = useWatch({
    control: control,
    name: ['startDate', 'endDate']
  });

  const {
    fields: dealHighlightFields,
    append: appendDealHighlight,
    remove: removeDealHighlights
  } = useFieldArray({
    control,
    name: 'preIPOHighlights'
  });

  const preIPOTypes = Object.keys(PreIPODealTypes).map(
    (key: keyof typeof PreIPODealTypes) => ({
      id: PreIPODealTypes[key],
      name: translateDynamic(`preIpoDeals.dealType.${PreIPODealTypes[key]}`)
    })
  );

  const dealModeTypes = Object.keys(PreIPOModeType).map(
    (key: keyof typeof PreIPOModeType) => ({
      id: PreIPOModeType[key],
      name: translateDynamic(`dealModeType.${DealModeType[key]}`)
    })
  );
  const deleteFile = (value: string) => {
    setDeleteClickedFile(value);
    setValue(
      value as never as keyof PreIPODealFormValues,
      {
        file: undefined,
        path: ''
      },
      { shouldDirty: true }
    );
  };

  const handleAddNewDealHighlight = () => {
    appendDealHighlight({
      id: '',
      title: '',
      description: '',
      sequence:
        dealHighlightFields.reduce(
          (max, obj) => (obj.sequence > max ? obj.sequence : max),
          0
        ) + 1
    });
  };

  const onChangeDealTermAmountFieldValue = (
    name: keyof PreIPODealFormValues,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (
      !(
        parseInt(getNumbersOnly(event.target.value)) >
        MAXIMUM_INVESTMENT_MAX_AMOUNT
      )
    ) {
      setValue(
        name as never as keyof PreIPODealFormValues,
        event.target.value,
        { shouldDirty: true }
      );
    }
  };

  const onISINTermChange = (
    name: keyof PreIPODealFormValues,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValue(name as never as keyof PreIPODealFormValues, event.target.value, {
      shouldDirty: true
    });
  };

  const onISINChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const val = event.target.value;
    if (val.match(RegularExpressions.isinRegex)) {
      setValue('isin', val, { shouldDirty: true });
      setValue('isinTerms', event.target.value, { shouldDirty: true });
    }
  };
  const onSetDealTerm = (setField: string, value: string) => {
    const val = Number.isNaN(parseFloat(value))
      ? value.substring(0, DEAL_TERMS_MAX_LENGTH)
      : formatAmount(parseFloat(getNumberFromString(value)));
    setValue(setField as never as keyof PreIPODealFormValues, val, {
      shouldDirty: true
    });
  };

  const handleRemoveDealHighlight = (index: number) => {
    removeDealHighlights(index);
    const currentPreIPOHighlights = getValues('preIPOHighlights');
    if (currentPreIPOHighlights.length === 0) {
      setValue('preIPOHighlights', [
        {
          id: '',
          title: '',
          description: '',
          sequence: 0
        }
      ]);
    }
  };

  const onChangeMinInvestment = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (
      !event.target.value ||
      parseInt(getNumbersOnly(event.target.value)) <=
        MINIMUM_INVESTMENT_MAX_AMOUNT
    ) {
      setValue(
        'minInvestment',
        getFormattedAmount(getNumbersOnly(event.target.value)),
        { shouldDirty: true }
      );
      onSetDealTerm('minInvestPreIPOTerm', event.target.value);
    }
  };

  const onChangeMaxInvestment = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (
      !event.target.value ||
      parseInt(getNumbersOnly(event.target.value)) <=
        MAXIMUM_INVESTMENT_MAX_AMOUNT
    ) {
      setValue(
        'maxInvestment',
        getFormattedAmount(getNumbersOnly(event.target.value)),
        { shouldDirty: true }
      );
      onSetDealTerm('maxInvestPreIPOTerm', event.target.value);
    }
  };

  useHotkeys('enter', () => handleNextClick());

  return (
    <div className="py-7 px-5">
      <FormProvider {...methods}>
        <form>
          <div className="flex flex-col">
            {/* Pre-IPO Details section */}
            <div className="flex flex-col border-b border-b-lightGrey">
              <div className="flex flex-col pb-14 mr-6 w-[67%]">
                <div className="text-xl font-semibold">
                  {translate(
                    'preIpoDeals.createPreIPODeals.preIPODetails.preIPODealDetails'
                  )}
                </div>
                <div className="flex flex-row">
                  <RhfDropdown
                    name="preIPOType"
                    control={control}
                    placeholder={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.preIPOType'
                    )}
                    dropdownLOV={preIPOTypes}
                    selectedItem={getValues('preIPOType')}
                    errors={errors}
                    wrapperClass="pt-6 mr-6 w-full"
                    description={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.preIPOTypeDescription'
                    )}
                    onChangeHandler={() => clearErrors('preIPOType')}
                  />
                  <RhfDropdown
                    name="preIPOMode"
                    control={control}
                    placeholder={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.preIPOMode'
                    )}
                    dropdownLOV={dealModeTypes}
                    selectedItem={getValues('preIPOMode')}
                    errors={errors}
                    wrapperClass="pt-6 w-full"
                    description={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.preIPOModeDescription'
                    )}
                    onChangeHandler={() => clearErrors('preIPOMode')}
                  />
                </div>
                <div className="flex flex-row">
                  <div className="flex-1 mr-6">
                    <RhfTextField
                      name="isin"
                      label={translate(
                        'preIpoDeals.createPreIPODeals.preIPODetails.isin'
                      )}
                      placeholder={translate(
                        'preIpoDeals.createPreIPODeals.preIPODetails.isinPlaceHolder'
                      )}
                      description={translate(
                        'preIpoDeals.createPreIPODeals.preIPODetails.isinDescription'
                      )}
                      defaultValue={getValues('isin')}
                      control={control}
                      wrapperClass="pt-6 w-full"
                      errors={errors}
                      onChange={onISINChange}
                      maxLength={12}
                      showCharacterLimit
                    />
                  </div>
                  <div className="flex-1"></div>
                </div>
              </div>
            </div>
            {/* Pre-IPO construct section */}
            <div className="flex flex-col border-b border-b-lightGrey">
              <div className="flex flex-col py-14 mr-6 w-[67%] ">
                <div className="text-xl font-semibold">
                  {translate(
                    'preIpoDeals.createPreIPODeals.preIPODetails.preIPODealConstruct'
                  )}
                </div>
                <div className="flex flex-row">
                  <RhfTextField
                    name={'preIPOName'}
                    label={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.preIPOName'
                    )}
                    placeholder={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.preIPONamePlaceHolder'
                    )}
                    errors={errors}
                    control={control}
                    wrapperClass={'pt-6  w-full'}
                    maxLength={400}
                    onChange={event => {
                      clearErrors('preIPOName');
                      setValue('preIPOName', event.target.value, {
                        shouldDirty: true
                      });
                    }}
                    showCharacterLimit
                  />
                </div>
                <div className="flex flex-row">
                  <DateTimePicker
                    name="startDate"
                    placeholder={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.startDate'
                    )}
                    control={control}
                    errors={errors}
                    defaultValue={
                      dealStartDate !== null
                        ? (dealStartDate as string)
                        : undefined
                    }
                    wrapperClass="pt-6 mr-6 w-full"
                    description={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.startDateDescription'
                    )}
                  />
                  <DateTimePicker
                    name="endDate"
                    placeholder={translate('deals.dealConstruct.endDate')}
                    control={control}
                    errors={errors}
                    defaultValue={
                      dealEndDate !== null ? dealEndDate : undefined
                    }
                    wrapperClass="pt-6 w-full"
                    description={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.endDateDescription'
                    )}
                  />
                </div>
              </div>
            </div>
            {/* Allocation Details Section */}

            <div className="flex flex-col border-b border-b-lightGrey">
              <div className="flex flex-col py-14 mr-6 w-[67%] ">
                <div className="text-xl font-semibold">
                  {translate(
                    'preIpoDeals.createPreIPODeals.preIPODetails.allocationDetails'
                  )}
                </div>
                <div className="flex flex-row gap-6">
                  <RhfTextField
                    name="minInvestment"
                    label={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.allocationMinInvestment'
                    )}
                    placeholder={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.allocationPlaceholder'
                    )}
                    control={control}
                    errors={errors}
                    wrapperClass="pt-6 w-full"
                    onClickTextField={() => {
                      clearErrors('minInvestment');
                    }}
                    onChange={onChangeMinInvestment}
                    description={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.allocationMinDescription'
                    )}
                    showCurrency
                    isIndianRupee
                  />
                  <RhfTextField
                    name="maxInvestment"
                    label={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.allocationMaxInvestment'
                    )}
                    placeholder={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.allocationPlaceholder'
                    )}
                    control={control}
                    errors={errors}
                    wrapperClass="pt-6 w-full mr-6"
                    onClickTextField={() => {
                      clearErrors('maxInvestment');
                    }}
                    onChange={onChangeMaxInvestment}
                    description={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.allocationMaxDescription'
                    )}
                    showCurrency
                    isIndianRupee
                    amount={getValues('maxInvestment')}
                  />
                </div>
              </div>
            </div>

            {/* Pre-IPO Terms Section */}
            <div className="flex flex-col border-b border-b-lightGrey">
              <div className="flex flex-col py-14 mr-6 w-[67%] ">
                <div className="text-xl font-semibold">
                  {translate(
                    'preIpoDeals.createPreIPODeals.preIPODetails.preIPODealTerms'
                  )}
                </div>
                <div className="flex flex-row">
                  <RhfTextField
                    name={'minInvestPreIPOTerm'}
                    label={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.minInvestment'
                    )}
                    placeholder={translate(
                      'deals.placeholders.dealTermsMinInvest'
                    )}
                    control={control}
                    errors={{
                      ['minInvestPreIPOTerm']: errors.minInvestPreIPOTerm
                    }}
                    wrapperClass="pt-6 mr-6 w-full"
                    onClickTextField={() => {
                      clearErrors('minInvestPreIPOTerm');
                    }}
                    description={translate(
                      'deals.descriptions.minInvestDealTerm'
                    )}
                    onChange={e =>
                      onChangeDealTermAmountFieldValue('minInvestPreIPOTerm', e)
                    }
                    maxLength={AMOUNT_FIELD_MAX_LENGTH}
                  />
                  <RhfTextField
                    name={'maxInvestPreIPOTerm'}
                    label={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.maxInvestment'
                    )}
                    placeholder={translate(
                      'deals.placeholders.dealTermsMinInvest'
                    )}
                    control={control}
                    errors={{
                      ['maxInvestPreIPOTerm']: errors.maxInvestPreIPOTerm
                    }}
                    wrapperClass="pt-6 w-full"
                    onClickTextField={() => {
                      clearErrors('maxInvestPreIPOTerm');
                    }}
                    description={translate(
                      'deals.descriptions.minInvestDealTerm'
                    )}
                    onChange={e =>
                      onChangeDealTermAmountFieldValue('maxInvestPreIPOTerm', e)
                    }
                    maxLength={AMOUNT_FIELD_MAX_LENGTH}
                  />
                </div>
                <div className="flex flex-row">
                  <RhfTextField
                    name={'settlementTime'}
                    label={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.settlementTime'
                    )}
                    placeholder={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.termsPlaceholder'
                    )}
                    errors={errors}
                    control={control}
                    wrapperClass={'pt-6  w-full mr-6'}
                    maxLength={13}
                    showCharacterLimit
                  />
                  <RhfTextField
                    name={'paymentDate'}
                    label={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.paymentDate'
                    )}
                    placeholder={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.termsPlaceholder'
                    )}
                    errors={errors}
                    control={control}
                    wrapperClass={'pt-6  w-full'}
                    maxLength={13}
                    showCharacterLimit
                  />
                </div>
                <div className="flex flex-row">
                  <RhfTextField
                    name="expectedIPODate"
                    label={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.expectedIPODate'
                    )}
                    placeholder={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.expectedIPODatePlaceholder'
                    )}
                    control={control}
                    wrapperClass="pt-6 w-full mr-6"
                    errors={errors}
                    maxLength={50}
                    showCharacterLimit
                  />
                  <RhfTextField
                    name="isinTerms"
                    label={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.isin'
                    )}
                    placeholder={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.isinPlaceHolder'
                    )}
                    defaultValue={getValues('isinTerms')}
                    control={control}
                    wrapperClass="pt-6 w-full"
                    errors={errors}
                    onChange={e => onISINTermChange('isinTerms', e)}
                    maxLength={12}
                    showCharacterLimit
                  />
                </div>
              </div>
            </div>

            {/* Pre-IPO Highlights */}
            <div className="flex flex-col border-b border-b-lightGrey">
              <div className="flex flex-col py-14 mr-6 w-full ">
                <div className="text-xl font-semibold">
                  {translate(
                    'preIpoDeals.createPreIPODeals.preIPODetails.preIPOHighlights'
                  )}
                </div>
                <div className="grid grid-cols-[minmax(0,150px)_minmax(0,350px)_auto_minmax(0,150px)] grid-flow-row  my-4">
                  {!!dealHighlightFields.length && (
                    <>
                      <div className="p-4 font-medium border border-grey88">
                        {translate(
                          'preIpoDeals.createPreIPODeals.preIPODetails.sNo'
                        )}
                      </div>
                      <div className="p-4 font-medium border border-grey88">
                        {translate(
                          'preIpoDeals.createPreIPODeals.preIPODetails.title'
                        )}
                      </div>
                      <div className="p-4 font-medium border border-grey88">
                        {translate(
                          'preIpoDeals.createPreIPODeals.preIPODetails.description'
                        )}
                      </div>
                      <div className="p-4 font-medium border border-grey88">
                        {translate(
                          'preIpoDeals.createPreIPODeals.preIPODetails.actions'
                        )}
                      </div>
                    </>
                  )}
                  {dealHighlightFields.map((field, index) => (
                    <Fragment key={field.id}>
                      <div className="flex items-center p-4 border border-grey88">
                        {translate(
                          'preIpoDeals.createPreIPODeals.preIPODetails.highlightCount',
                          {
                            count: index + 1
                          }
                        )}
                      </div>

                      <RhfTextField
                        name={`preIPOHighlights.${index}.title`}
                        control={control}
                        errors={{
                          [`preIPOHighlights.${index}.title`]:
                            errors.preIPOHighlights?.[index]?.title
                        }}
                        multiline={true}
                        rows={4}
                        wrapperClass="w-full"
                        onClickTextField={() => {
                          clearErrors(`preIPOHighlights.${index}.title`);
                        }}
                        maxLength={DEAL_HIGHLIGHTS_TITLE_LENGTH}
                        showCharacterLimit
                        removeBorderRadius
                        hideHelperText
                      />

                      <RhfTextField
                        name={`preIPOHighlights.${index}.description`}
                        control={control}
                        errors={{
                          [`preIPOHighlights.${index}.description`]:
                            errors.preIPOHighlights?.[index]?.description
                        }}
                        multiline={true}
                        rows={4}
                        wrapperClass="w-full"
                        onClickTextField={() => {
                          clearErrors(`preIPOHighlights.${index}.description`);
                        }}
                        maxLength={800}
                        showCharacterLimit={true}
                        removeBorderRadius
                        hideHelperText
                      />
                      <div className="flex gap-10 justify-start items-center p-4 w-full border  border-grey88">
                        <TrashIcon
                          className="w-6 h-6 cursor-pointer trash-icon"
                          onClick={() => handleRemoveDealHighlight(index)}
                        />
                      </div>
                    </Fragment>
                  ))}
                </div>
                <div>
                  <div className="group inline-flex">
                    <Button
                      variant="outlined"
                      color="info"
                      size="small"
                      style={{
                        marginTop: 16
                      }}
                      className="group-hover:text-white group-hover:bg-brightBlue"
                      startIcon={<PlusIcon />}
                      onClick={handleAddNewDealHighlight}>
                      {translate(
                        'preIpoDeals.createPreIPODeals.preIPODetails.addHighlight'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Pre-IPO Visual Assets */}
            <div className="flex flex-col ">
              <div className="flex flex-col py-14 mr-6 w-full ">
                <div className="text-xl font-semibold">
                  {translate(
                    'preIpoDeals.createPreIPODeals.preIPODetails.visualAssets'
                  )}
                </div>
                <div className="flex flex-row gap-3 w-full">
                  {/* cover image */}
                  <div className="pt-4 w-full">
                    <div className="flex flex-row items-center pb-1 text-base font-normal text-black/60">
                      {translate(
                        'preIpoDeals.createPreIPODeals.preIPODetails.coverImage'
                      )}
                      <Tooltip
                        title={translate(
                          'preIpoDeals.createPreIPODeals.preIPODetails.visualAssetsCoverImageInfoText'
                        )}
                        arrow={true}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: '12px'
                            }
                          }
                        }}>
                        <InfoIcon
                          height={16}
                          width={16}
                          className="ml-1 cursor-pointer"
                          fill={colors.black60}
                        />
                      </Tooltip>
                    </div>
                    {deleteClickedFile === 'coverImage' ||
                    !getValues('coverImage').file ? (
                      <FileUploader
                        name="coverImage"
                        enableCrop={true}
                        sizeInMb={3}
                        supportedFormats={IMAGE_FORMATS}
                        fileUploadSuccessHandler={(file, path) => {
                          setValue(
                            'coverImage',
                            { file: file, path: path },
                            { shouldDirty: true }
                          );
                          clearErrors('coverImage');
                          setDeleteClickedFile('');
                        }}
                        deleteFileHandler={() =>
                          setValue(
                            'coverImage',
                            { file: undefined, path: '' },
                            { shouldDirty: true }
                          )
                        }
                        errors={errors}
                        previousFile={getValues('coverImage').file}
                        aspectRatio={16 / 9}
                        imageInfo={translate(
                          'deals.visualAssetsCoverImageInfoText'
                        )}
                        showPreview
                        customUploadText={translate('deals.uploadImageText', {
                          aspectRatio: '16:9'
                        })}
                        description={translate(
                          'preIpoDeals.createPreIPODeals.preIPODetails.uploadAssetsCoverImage'
                        )}
                      />
                    ) : (
                      <ImagePreviewCard
                        name={'coverImage'}
                        isEdit={true}
                        handleDeleteClick={() => deleteFile('coverImage')}
                        imageUrl={generateFileUrl(
                          getValues('coverImage.path'),
                          getValues('coverImage.file')
                        )}
                        fileName={getValues('coverImage')?.file?.name || ''}
                      />
                    )}
                  </div>
                  {/* background image */}
                  <div className="invisible pt-4 w-full">
                    <div className="flex flex-row items-center pb-1 text-base font-normal text-black/60">
                      {translate(
                        'preIpoDeals.createPreIPODeals.preIPODetails.backgroundImage'
                      )}
                    </div>
                    {deleteClickedFile === 'backgroundImage' ||
                    !getValues('backgroundImage').file ? (
                      <FileUploader
                        name="backgroundImage"
                        enableCrop={true}
                        sizeInMb={3}
                        supportedFormats={IMAGE_FORMATS}
                        fileUploadSuccessHandler={(file, path) => {
                          setValue(
                            'backgroundImage',
                            { file: file, path: path },
                            { shouldDirty: true }
                          );
                          clearErrors('backgroundImage');
                          setDeleteClickedFile('');
                        }}
                        deleteFileHandler={() =>
                          setValue(
                            'backgroundImage',
                            { file: undefined, path: '' },
                            { shouldDirty: true }
                          )
                        }
                        errors={errors}
                        previousFile={getValues('backgroundImage').file}
                        aspectRatio={9 / 16}
                        showPreview
                        customUploadText={translate('deals.uploadImageText', {
                          aspectRatio: '9:16'
                        })}
                      />
                    ) : (
                      <ImagePreviewCard
                        name={'backgroundImage'}
                        isEdit={true}
                        handleDeleteClick={() => deleteFile('backgroundImage')}
                        imageUrl={generateFileUrl(
                          getValues('backgroundImage.path'),
                          getValues('backgroundImage.file')
                        )}
                        fileName={
                          getValues('backgroundImage')?.file?.name || ''
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end py-4">
            <DualButton
              isPrimaryDisabled={isPreIPOBottomActionDisabled(
                getValues('status') as PreIPODealStatus
              )}
              isSecondaryDisabled={isPreIPOBottomActionDisabled(
                getValues('status') as PreIPODealStatus
              )}
              primaryButtonLabel={translate('next')}
              secondaryButtonLabel={translate('saveAsDraft')}
              onChangePrimaryButton={handleNextClick}
              isSecondaryButtonLoading={isUpdatePreIPOLoading}
              onChangeSecondaryButton={handleSaveAsDraftClick}
              secondaryButtonCustomStyle={{
                backgroundColor: colors.gray88,
                color: colors.black,
                width: '136px'
              }}
            />
          </div>
        </form>
      </FormProvider>
      <Backdrop
        sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={isGetPreIPOLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default PreIPODealDetailsForm;
